import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Scaffold } from "../Scaffold";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Tooltip,
} from "@mui/material";
import { CheckCircle, Cancel, HelpOutline } from "@mui/icons-material";
import { State } from "../redux/types";
import { vendorRequest } from "../api/api";
import { setAdvanceResult } from "../redux/actions";
import { maskPhone } from "../utils/masking";
import LoadingSpinner from "../LoadingSpinner";

// Coerce unknown to boolean | null for status (avoids nested ternary in JSX)
const toFlaggedForDeletion = (v: unknown): boolean | null => {
  if (v === true) {
    return true;
  }
  if (v === false) {
    return false;
  }
  return null;
};

// Safe string for keys/display to satisfy Sonar (avoid [object Object] from non-primitives)
function safeStr(v: unknown): string {
  if (typeof v === "string") {
    return v;
  }
  if (v === null || v === undefined) {
    return "";
  }
  return typeof v === "number" || typeof v === "boolean" ? String(v) : "";
}

// Helper: determine if a vendor row is accessible (httpCode 200 and not flagged)
const isRowAccessible = (row: Record<string, unknown>): boolean => {
  const httpCode = row.httpCode as number | undefined;
  if (httpCode !== undefined && httpCode !== 200) {
    return false;
  }
  if (row.flaggedForDeletion === true) {
    return false;
  }
  return Boolean(row.customerVendorId);
};

// Helper function to render status icon based on httpCode and flaggedForDeletion
const getStatusIcon = (row: Record<string, unknown>) => {
  const httpCode = row.httpCode as number | undefined;
  const flaggedForDeletion = toFlaggedForDeletion(row.flaggedForDeletion);

  if (flaggedForDeletion === true) {
    return (
      <Tooltip title="Flagged for Deletion - Cannot access">
        <Cancel sx={{ color: "#d32f2f", fontSize: 20 }} />
      </Tooltip>
    );
  }
  if (httpCode !== undefined && httpCode !== 200) {
    return (
      <Tooltip title="Not Found">
        <HelpOutline sx={{ color: "#ed6c02", fontSize: 20 }} />
      </Tooltip>
    );
  }
  if (flaggedForDeletion === false) {
    return (
      <Tooltip title="Active Record">
        <CheckCircle sx={{ color: "#2e7d32", fontSize: 20 }} />
      </Tooltip>
    );
  }
  return (
    <Tooltip title="Status Unknown">
      <HelpOutline sx={{ color: "#ed6c02", fontSize: 20 }} />
    </Tooltip>
  );
};

export const AdvanceResult = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const result = useSelector((state: State) => state.result);
  const [isLoading, setIsLoading] = useState(false);

  const handleRowClick = async (row: Record<string, unknown>) => {
    if (!isRowAccessible(row)) {
      return;
    }

    const vendorId = safeStr(row.customerVendorId);
    const vendorKey = safeStr(row.vendorKey);

    const normalizedVendorKey = vendorKey.toLowerCase();
    setIsLoading(true);
    dispatch(setAdvanceResult(null));

    try {
      const response = await vendorRequest(vendorId, normalizedVendorKey);
      dispatch(setAdvanceResult(response));
      const route = vendorKey.toLowerCase();
      if (response.value) {
        navigate(`/${route}`, {
          state: {
            vendorKey,
            customerVendorId: vendorId,
            customerData: response.value
          },
        });
      } else {
        navigate(`/${route}`, {
          state: {
            vendorKey,
            customerVendorId: vendorId,
            error: response.error
          },
        });
      }
    } catch (error) {
      console.warn("vendorRequest failed:", error);
      const route = vendorKey.toLowerCase();
      navigate(`/${route}`, {
        state: {
          vendorKey,
          customerVendorId: vendorId,
          error: "Failed to fetch customer data"
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Normalize: API may return { searchResults } or store may be null (direct nav / refresh)
  type ResultRow = Record<string, unknown>;
  const searchResults = (result && typeof result === "object" && "searchResults" in result)
    ? (result as { searchResults: unknown[] }).searchResults
    : [];
  const safeSearchResults: ResultRow[] = Array.isArray(searchResults) ? (searchResults as ResultRow[]) : [];

  const emailResults = safeSearchResults.filter(
    (row: ResultRow) =>
      row.searchKey === "email" ||
      row.searchKey === "Email" ||
      row.searchKey === "emailAddress"
  );
  const phoneResults = safeSearchResults.filter(
    (row: ResultRow) => row.searchKey === "MobilePhoneNumber"
  );

  // Get search values for display
  const emailValue =
    emailResults.length > 0 ? emailResults[0].searchValue : null;
  const phoneValue =
    phoneResults.length > 0 ? phoneResults[0].searchValue : null;
  const formattedPhoneValue =
    phoneValue === null || phoneValue === undefined
      ? "N/A"
      : (maskPhone(safeStr(phoneValue)) || safeStr(phoneValue));

  return (
    <Scaffold center navBar>
      {isLoading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            bgcolor: "rgba(255,255,255,0.7)",
          }}
        >
          <LoadingSpinner />
        </Box>
      )}
      <Box sx={{ width: "100%", maxWidth: "1200px", mx: "auto", px: 3, py: 4 }}>
        <Typography variant="h4" sx={{ textAlign: "center", mb: 4, fontWeight: "bold" }}>
          Advanced Search Results
        </Typography>

        {/* Email Results Table */}
        {emailResults.length > 0 && (
          <Box sx={{ marginBottom: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: "#333" }}>
              Email Results for {emailValue === null || emailValue === undefined ? "N/A" : safeStr(emailValue)}
            </Typography>
            <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
              <Table sx={{ minWidth: 450 }} aria-label="email results table">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Vendor</TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {emailResults.map((searchResult: ResultRow) =>
                  (Array.isArray(searchResult.resultValues) ? (searchResult.resultValues as ResultRow[]) : []).map(
                    (row: ResultRow) => (
                    <TableRow
                      key={`email-${safeStr(row.vendorKey)}-${safeStr(row.customerVendorId)}-${safeStr(row.firstName)}-${safeStr(row.lastName)}-${safeStr(row.mobilePhoneNumber)}`}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        cursor: isRowAccessible(row) ? "pointer" : "not-allowed",
                        "&:hover": { backgroundColor: isRowAccessible(row) ? "rgba(0, 0, 0, 0.04)" : "rgba(211, 47, 47, 0.08)" },
                        opacity: isRowAccessible(row) ? 1 : 0.6,
                      }}
                      onClick={() => handleRowClick(row)}
                    >
                    <TableCell sx={{ py: 1.5 }}>{getStatusIcon(row)}</TableCell>
                    <TableCell component="th" scope="row" sx={{ py: 1.5 }}>
                      {safeStr(row.firstName)} {safeStr(row.lastName)}
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>{maskPhone(safeStr(row.mobilePhoneNumber)) || "N/A"}</TableCell>
                    <TableCell align="right" sx={{ py: 1.5 }}>{safeStr(row.vendorKey)}</TableCell>
                    </TableRow>
                  )
                )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

        {/* Phone Results Table */}
        {phoneResults.length > 0 && (
          <Box sx={{ marginBottom: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: "#333" }}>
              Phone Results for {String(formattedPhoneValue)}
            </Typography>
            <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
              <Table sx={{ minWidth: 450 }} aria-label="phone results table">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Vendor</TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {phoneResults.map((searchResult: ResultRow) =>
                  (Array.isArray(searchResult.resultValues) ? (searchResult.resultValues as ResultRow[]) : []).map(
                    (row: ResultRow) => (
                    <TableRow
                      key={`phone-${safeStr(row.vendorKey)}-${safeStr(row.customerVendorId)}-${safeStr(row.firstName)}-${safeStr(row.lastName)}-${safeStr(row.emailAddress)}`}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        cursor: isRowAccessible(row) ? "pointer" : "not-allowed",
                        "&:hover": { backgroundColor: isRowAccessible(row) ? "rgba(0, 0, 0, 0.04)" : "rgba(211, 47, 47, 0.08)" },
                        opacity: isRowAccessible(row) ? 1 : 0.6,
                      }}
                      onClick={() => handleRowClick(row)}
                    >
                    <TableCell sx={{ py: 1.5 }}>{getStatusIcon(row)}</TableCell>
                    <TableCell component="th" scope="row" sx={{ py: 1.5 }}>
                      {safeStr(row.firstName)} {safeStr(row.lastName)}
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>{row.emailAddress === null || row.emailAddress === undefined ? "N/A" : safeStr(row.emailAddress)}</TableCell>
                    <TableCell align="right" sx={{ py: 1.5 }}>{safeStr(row.vendorKey)}</TableCell>
                    </TableRow>
                  )
                )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

        {/* Show message if no results */}
        {emailResults.length === 0 && phoneResults.length === 0 && (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No search results found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please try a different search query
            </Typography>
          </Box>
        )}
      </Box>
    </Scaffold>
  );
};
